import { useQueryClient } from "@tanstack/react-query";

import type { OpportunityWindow, TradeSnapshot } from "@/generated/api/model";

const LOVELACE_PER_ADA = 1_000_000;

export const ada = (lovelace: number): string => (lovelace / LOVELACE_PER_ADA).toFixed(2);

export const toLovelace = (adaAmount: number): number => Math.round(adaAmount * LOVELACE_PER_ADA);

/** `45s`, `3m 20s`, `1h 02m`, `2d 4h`: the two most significant units. */
export function duration(secs: number): string {
    const d = Math.floor(secs / 86_400);
    const h = Math.floor((secs % 86_400) / 3_600);
    const m = Math.floor((secs % 3_600) / 60);
    const s = secs % 60;
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
    if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
    return `${s}s`;
}

/** Chain time as a UTC timestamp, the clock the stats are bucketed by. */
export const chainTime = (unix: number): string =>
    `${new Date(unix * 1000).toISOString().slice(0, 19).replace("T", " ")} UTC`;

export const route = (trade: TradeSnapshot): string => `${trade.buy_dex} → ${trade.sell_dex}`;

/** Approximate bytes formatted as B, KB, MB, GB. */
export function formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024 * 1024) {
        return `${Math.round(bytes / (1024 * 1024 * 1024))} GB`;
    }
    if (bytes >= 1024 * 1024) {
        return `${Math.round(bytes / (1024 * 1024))} MB`;
    }
    if (bytes >= 1024) {
        return `${Math.round(bytes / 1024)} KB`;
    }
    return `${bytes} B`;
}

/**
 * Overview footer summarizing store retention, stored window count, disk size,
 * and oldest recorded window opening.
 * E.g. "Keeping 365 days · 12 840 windows · 38 MB · oldest 2025-10-11"
 */
export function retentionFooter(stats: {
    retention_days: number;
    stored_windows: number;
    store_bytes: number;
    oldest_opened_at?: number | null;
}): string {
    const retention =
        stats.retention_days === 0
            ? "Keeping everything"
            : `Keeping ${stats.retention_days} ${stats.retention_days === 1 ? "day" : "days"}`;
    const windowCount = stats.stored_windows.toLocaleString("en-US").replace(/,/g, " ");
    const windows = `${windowCount} ${stats.stored_windows === 1 ? "window" : "windows"}`;
    const size = formatBytes(stats.store_bytes);
    const parts = [retention, windows, size];
    if (stats.oldest_opened_at) {
        const oldestDate = new Date(stats.oldest_opened_at * 1000).toISOString().slice(0, 10);
        parts.push(`oldest ${oldestDate}`);
    }
    return parts.join(" · ");
}

export const windowEnd = (window: OpportunityWindow): number => window.closed_at ?? window.last_seen_at;

/** Every opportunity query, so a threshold change re-classifies all of them at once. */
export function useInvalidateOpportunities() {
    const queryClient = useQueryClient();
    return () =>
        queryClient.invalidateQueries({
            predicate: (query) => String(query.queryKey[0]).startsWith("/api/opportunities"),
        });
}

type CsvRow = readonly (string | number)[];

export function toCsv(header: CsvRow, rows: readonly CsvRow[]): string {
    const cell = (value: string | number) => {
        // Token names come from on-chain metadata: a leading `=`/`+`/`-`/`@` would run as a
        // formula when the file is opened in a spreadsheet, so such text is forced to a string.
        const text = typeof value === "string" && /^[=+\-@\t\r]/.test(value) ? `'${value}` : String(value);
        return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
    };
    return [header, ...rows].map((row) => row.map(cell).join(",")).join("\n") + "\n";
}

export function downloadCsv(filename: string, csv: string) {
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}
